type CoachVoiceCustomizationStatus = "customized" | "framework_default" | "default";

interface CoachFrameworkPackSnapshot {
  id: string;
  label: string;
  family: string;
  source: string;
  preview: {
    philosophySummary?: string | null;
    whatItNotices?: string[] | null;
    watchouts?: string[] | null;
    sampleCoachLanguage?: string[] | null;
    sampleLeaderLanguage?: string[] | null;
  };
  runtime: {
    interventionPatterns?: string[] | null;
    progressMarkers?: string[] | null;
    stallSignals?: string[] | null;
  };
  operationalization?: {
    noticingSignals?: Array<{
      label?: string | null;
      prohibitedInferences?: string[] | null;
    }> | null;
    reflectionPatterns?: Array<{
      label?: string | null;
    }> | null;
    boundaries: {
      allowed: string[];
      prohibited: string[];
      coachOnly: string[];
    };
  };
}

interface CoachLensSource {
  lens_name?: string | null;
  frameworks?: Array<{ name?: string | null; summary?: string | null }> | null;
  preferred_language?: string[] | null;
  avoid_language?: string[] | null;
  coach_lens_json?: {
    philosophy?: string | null;
    frameworks_raw?: string | null;
    frameworks_normalized?: string | null;
    framework_pack_id?: string | null;
    framework_pack?: unknown;
    identity_report?: {
      referenceCount?: number | null;
      parsedReferenceCount?: number | null;
    } | null;
  } | null;
}

interface CoachTwinSource {
  voice_summary?: string | null;
  question_stems?: string[] | null;
  phrase_bank?: string[] | null;
  avoid_list?: string[] | null;
  guardrails?: string[] | null;
  coaching_moves?: string[] | null;
  identity_template_preview?: {
    lensLabel?: string | null;
    philosophySummary?: string | null;
    voiceSummary?: string | null;
    frameworkPackId?: string | null;
    frameworkPackLabel?: string | null;
    voiceCustomizationStatus?: CoachVoiceCustomizationStatus | null;
    referenceCount?: number | null;
    parsedReferenceCount?: number | null;
  } | null;
}

interface CoachDigitalTwinRuntime {
  identity: {
    philosophy: {
      lens: {
        lensName: string | null;
        frameworkPackId?: string | null;
        frameworkPackLabel?: string | null;
        frameworkPack?: CoachFrameworkPackSnapshot | null;
      };
    };
    voice: {
      voiceSummary: string | null;
      customizationStatus?: CoachVoiceCustomizationStatus;
      questionStems: string[];
      phraseBank: string[];
    };
    ingestionReport: {
      referenceCount: number;
      parsedReferenceCount: number;
    };
  };
  philosophy: {
    lens: {
      lensName: string | null;
      frameworkPackId?: string | null;
      frameworkPackLabel?: string | null;
      frameworkPack?: CoachFrameworkPackSnapshot | null;
    };
  };
  voice: {
    voiceSummary: string | null;
    customizationStatus?: CoachVoiceCustomizationStatus;
  };
  expressionPolicy: {
    preferredLanguage: string[];
    avoidLanguage: string[];
    guardrails: string[];
  };
}

type CoachVeeraIdentityLensSource = CoachLensSource & {
  question_stems?: string[] | null;
};

export interface CoachVeeraIdentityContext {
  lensLabel: string | null;
  frameworkPackId: string | null;
  frameworkPackLabel: string | null;
  frameworkPack: CoachFrameworkPackSnapshot | null;
  frameworkSummary: string | null;
  frameworkNotices: string[];
  frameworkWatchouts: string[];
  frameworkInterventionPatterns: string[];
  frameworkProgressMarkers: string[];
  frameworkStallSignals: string[];
  frameworkNoticingSignals: string[];
  frameworkReflectionPatterns: string[];
  frameworkAllowedLanguage: string[];
  frameworkProhibitedInferences: string[];
  frameworkCoachOnlyBoundaries: string[];
  coachLanguageHints: string[];
  voiceSummary: string | null;
  voiceCustomizationStatus: CoachVoiceCustomizationStatus;
  referenceCount: number;
  parsedReferenceCount: number;
  preferredLanguage: string[];
  avoidLanguage: string[];
  guardrails: string[];
  questionStems: string[];
  phraseBank: string[];
  coachingMoves: string[];
}

function trimText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function dedupe(values: Array<string | null | undefined>, limit = 8): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    const normalized = trimText(value);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
    if (result.length >= limit) break;
  }

  return result;
}

function normalizeStringArray(values: string[] | null | undefined, limit = 8): string[] {
  return dedupe(values || [], limit);
}

function normalizeLabelArray(values: Array<{ label?: string | null } | null | undefined> | null | undefined, limit = 8): string[] {
  return dedupe((values || []).map((value) => value?.label || null), limit);
}

function normalizeProhibitedInferences(
  values: Array<{ prohibitedInferences?: string[] | null } | null | undefined> | null | undefined,
  limit = 10,
): string[] {
  return dedupe((values || []).flatMap((value) => value?.prohibitedInferences || []), limit);
}

function normalizeComparisonLabel(value: string | null | undefined): string | null {
  const normalized = trimText(value)?.toLowerCase().replace(/[^a-z0-9]+/g, "");
  return normalized || null;
}

const DENO_SAFE_FRAMEWORK_PACK_TEXT_LOOKUP: Array<{ id: string; tokens: string[] }> = [
  {
    id: "marshall_goldsmith_stakeholder_centered",
    tokens: [
      "marshall goldsmith stakeholder centered coaching",
      "stakeholder centered coaching",
      "marshall goldsmith",
    ],
  },
  {
    id: "grow",
    tokens: ["grow", "grow model"],
  },
  {
    id: "immunity_to_change",
    tokens: ["immunity to change"],
  },
  {
    id: "solution_focused",
    tokens: ["solution-focused coaching", "solution focused coaching", "solution-focused", "solution focused"],
  },
  {
    id: "co_active",
    tokens: ["co-active coaching", "co-active", "coactive coaching", "coactive"],
  },
  {
    id: "authentic_leadership",
    tokens: ["authentic leadership"],
  },
  {
    id: "radical_candor",
    tokens: ["radical candor", "care personally challenge directly"],
  },
  {
    id: "adaptive_leadership",
    tokens: ["adaptive leadership", "adaptive leadership model"],
  },
  {
    id: "executive_presence",
    tokens: ["executive presence", "leadership presence"],
  },
  {
    id: "inclusive_leadership",
    tokens: ["inclusive leadership", "juliet bourke inclusive leadership"],
  },
  {
    id: "servant_leadership",
    tokens: ["servant leadership", "greenleaf servant leadership"],
  },
];

function normalizeFrameworkPackLookupText(value: string | null | undefined): string | null {
  const normalized = trimText(value)?.toLowerCase().replace(/\s+/g, " ");
  return normalized || null;
}

function findDenoSafeFrameworkPackIdByText(value: string | null | undefined): string | null {
  const normalized = normalizeFrameworkPackLookupText(value);
  if (!normalized) return null;

  const match = DENO_SAFE_FRAMEWORK_PACK_TEXT_LOOKUP.find((candidate) =>
    candidate.tokens.some((token) => normalizeFrameworkPackLookupText(token) === normalized),
  );

  return match?.id || null;
}

function normalizedFrameworkLabelCandidates(value: string | null | undefined): Set<string> {
  const normalizedText = trimText(value)?.toLowerCase().replace(/[^a-z0-9]+/g, " ");
  const tokens = normalizedText?.split(/\s+/).filter(Boolean) || [];
  const candidates = new Set<string>();
  const addCandidate = (candidateTokens: string[]) => {
    const candidate = normalizeComparisonLabel(candidateTokens.join(" "));
    if (candidate) candidates.add(candidate);
  };

  addCandidate(tokens);
  if (tokens.length > 1 && ["framework", "method", "model"].includes(tokens[tokens.length - 1])) {
    addCandidate(tokens.slice(0, -1));
  }

  return candidates;
}

function firstDeclaredFrameworkLabel(coachLens?: CoachVeeraIdentityLensSource | null): string | null {
  const rawFrameworks = trimText(
    coachLens?.coach_lens_json?.frameworks_raw
    || coachLens?.coach_lens_json?.frameworks_normalized
    || null,
  );
  if (rawFrameworks) {
    const first = rawFrameworks.split(/[\n,;]+/).map((item) => trimText(item)).find(Boolean);
    if (first) return first;
  }

  return trimText(coachLens?.frameworks?.[0]?.name);
}

function isPersistedFrameworkPackCurrent(params: {
  frameworkPack: CoachFrameworkPackSnapshot | null;
  declaredFrameworkLabel: string | null;
}): boolean {
  if (!params.frameworkPack || !params.declaredFrameworkLabel) return true;
  const declaredFrameworkPackId = findDenoSafeFrameworkPackIdByText(params.declaredFrameworkLabel);
  if (declaredFrameworkPackId) {
    return declaredFrameworkPackId === params.frameworkPack.id;
  }
  const normalizedPackLabel = normalizeComparisonLabel(params.frameworkPack.label);
  return Boolean(
    normalizedPackLabel
    && normalizedFrameworkLabelCandidates(params.declaredFrameworkLabel).has(normalizedPackLabel)
  );
}

export function extractPersistedFrameworkPackSnapshot(
  coachLens?: CoachLensSource | null,
): CoachFrameworkPackSnapshot | null {
  const candidate = coachLens?.coach_lens_json?.framework_pack;
  if (!isRecord(candidate)) return null;

  const preview = candidate.preview;
  const runtime = candidate.runtime;
  if (
    typeof candidate.id !== "string"
    || typeof candidate.label !== "string"
    || typeof candidate.family !== "string"
    || typeof candidate.source !== "string"
    || !preview
    || typeof preview !== "object"
    || !runtime
    || typeof runtime !== "object"
  ) {
    return null;
  }

  return candidate as unknown as CoachFrameworkPackSnapshot;
}

export function resolvePersistedVoiceCustomizationStatus(
  coachTwin?: CoachTwinSource | null,
): CoachVoiceCustomizationStatus {
  const status = coachTwin?.identity_template_preview?.voiceCustomizationStatus;
  if (status === "customized" || status === "framework_default" || status === "default") {
    return status;
  }
  return "default";
}

function buildFrameworkMetadata(params: {
  frameworkPack: CoachFrameworkPackSnapshot | null;
  frameworkPackId?: string | null;
  frameworkPackLabel?: string | null;
  frameworkSummary?: string | null;
}) {
  return {
    frameworkPackId: params.frameworkPack?.id || trimText(params.frameworkPackId),
    frameworkPackLabel: params.frameworkPack?.label || trimText(params.frameworkPackLabel),
    frameworkPack: params.frameworkPack,
    frameworkSummary: trimText(params.frameworkPack?.preview.philosophySummary || params.frameworkSummary),
    frameworkNotices: normalizeStringArray(params.frameworkPack?.preview.whatItNotices, 6),
    frameworkWatchouts: normalizeStringArray(params.frameworkPack?.preview.watchouts, 6),
    frameworkInterventionPatterns: normalizeStringArray(params.frameworkPack?.runtime.interventionPatterns, 6),
    frameworkProgressMarkers: normalizeStringArray(params.frameworkPack?.runtime.progressMarkers, 6),
    frameworkStallSignals: normalizeStringArray(params.frameworkPack?.runtime.stallSignals, 6),
    frameworkNoticingSignals: normalizeLabelArray(params.frameworkPack?.operationalization?.noticingSignals, 6),
    frameworkReflectionPatterns: normalizeLabelArray(params.frameworkPack?.operationalization?.reflectionPatterns, 6),
    frameworkAllowedLanguage: normalizeStringArray(params.frameworkPack?.operationalization?.boundaries.allowed, 8),
    frameworkProhibitedInferences: normalizeProhibitedInferences(params.frameworkPack?.operationalization?.noticingSignals, 10),
    frameworkCoachOnlyBoundaries: normalizeStringArray(params.frameworkPack?.operationalization?.boundaries.coachOnly, 8),
    coachLanguageHints: normalizeStringArray(params.frameworkPack?.preview.sampleCoachLanguage, 6),
  };
}

export function buildCoachVeeraIdentityContextFromRuntime(
  coachRuntime: CoachDigitalTwinRuntime,
): CoachVeeraIdentityContext {
  const identity = coachRuntime.identity;
  const philosophyLens = identity.philosophy.lens;
  const runtimeLens = coachRuntime.philosophy.lens;
  const frameworkPack = philosophyLens.frameworkPack || runtimeLens.frameworkPack || null;
  const frameworkMetadata = buildFrameworkMetadata({
    frameworkPack,
    frameworkPackId: philosophyLens.frameworkPackId || runtimeLens.frameworkPackId || null,
    frameworkPackLabel: philosophyLens.frameworkPackLabel || runtimeLens.frameworkPackLabel || null,
  });

  return {
    lensLabel: trimText(philosophyLens.lensName || runtimeLens.lensName),
    ...frameworkMetadata,
    voiceSummary: trimText(identity.voice.voiceSummary || coachRuntime.voice.voiceSummary),
    voiceCustomizationStatus: identity.voice.customizationStatus
      || coachRuntime.voice.customizationStatus
      || "default",
    referenceCount: identity.ingestionReport.referenceCount,
    parsedReferenceCount: identity.ingestionReport.parsedReferenceCount,
    preferredLanguage: normalizeStringArray(coachRuntime.expressionPolicy.preferredLanguage, 6),
    avoidLanguage: normalizeStringArray(coachRuntime.expressionPolicy.avoidLanguage, 8),
    guardrails: normalizeStringArray(coachRuntime.expressionPolicy.guardrails, 6),
    questionStems: normalizeStringArray(identity.voice.questionStems, 5),
    phraseBank: normalizeStringArray(identity.voice.phraseBank, 5),
    coachingMoves: [],
  };
}

export function buildCoachVeeraIdentityContextFromSources(params: {
  coachLens?: CoachVeeraIdentityLensSource | null;
  coachTwin?: CoachTwinSource | null;
}): CoachVeeraIdentityContext {
  const persistedFrameworkPack = extractPersistedFrameworkPackSnapshot(params.coachLens);
  const declaredFrameworkLabel = firstDeclaredFrameworkLabel(params.coachLens);
  const frameworkPack = isPersistedFrameworkPackCurrent({
    frameworkPack: persistedFrameworkPack,
    declaredFrameworkLabel,
  })
    ? persistedFrameworkPack
    : null;
  const twinPreview = params.coachTwin?.identity_template_preview;
  const frameworkMetadata = buildFrameworkMetadata({
    frameworkPack,
    frameworkPackId: frameworkPack
      ? params.coachLens?.coach_lens_json?.framework_pack_id || twinPreview?.frameworkPackId || null
      : null,
    frameworkPackLabel: declaredFrameworkLabel || twinPreview?.frameworkPackLabel || null,
    frameworkSummary: params.coachLens?.coach_lens_json?.philosophy || twinPreview?.philosophySummary || null,
  });
  const persistedVoiceCustomizationStatus = resolvePersistedVoiceCustomizationStatus(params.coachTwin);
  const voiceSummary = trimText(
    params.coachTwin?.voice_summary
    || twinPreview?.voiceSummary
    || null,
  );
  const voiceCustomizationStatus = persistedVoiceCustomizationStatus !== "default"
    ? persistedVoiceCustomizationStatus
    : voiceSummary
      ? "customized"
      : "default";
  const referenceCount = twinPreview?.referenceCount
    ?? params.coachLens?.coach_lens_json?.identity_report?.referenceCount
    ?? 0;
  const parsedReferenceCount = twinPreview?.parsedReferenceCount
    ?? params.coachLens?.coach_lens_json?.identity_report?.parsedReferenceCount
    ?? 0;

  return {
    lensLabel: trimText(
      params.coachLens?.lens_name
      || twinPreview?.lensLabel
      || params.coachTwin?.identity_template_preview?.lensLabel
      || null,
    ),
    ...frameworkMetadata,
    voiceSummary,
    voiceCustomizationStatus,
    referenceCount,
    parsedReferenceCount,
    preferredLanguage: normalizeStringArray(params.coachLens?.preferred_language, 6),
    avoidLanguage: dedupe([
      ...(params.coachLens?.avoid_language || []),
      ...(params.coachTwin?.avoid_list || []),
      ...(params.coachTwin?.guardrails || []),
      ...((params.coachLens?.coach_lens_json as { avoid?: string[] | null } | null)?.avoid || []),
    ], 8),
    guardrails: normalizeStringArray(params.coachTwin?.guardrails, 6),
    questionStems: dedupe([
      ...(params.coachLens?.question_stems || []),
      ...(params.coachTwin?.question_stems || []),
    ], 5),
    phraseBank: normalizeStringArray(params.coachTwin?.phrase_bank, 5),
    coachingMoves: normalizeStringArray(params.coachTwin?.coaching_moves, 5),
  };
}
